import { useEffect, useMemo } from "react";

import { useConfig } from "@jti/app-context/useConfig";

import { getInitialDataFromURL } from "../../utils/convertSharedFilterData";
import { useFlotiqStorageConfig } from "../useFlotiqStorageConfig";
import { useFlotiqStores } from "../useFlotiqStores/useFlotiqStores";
import { useGoogleMapsStoreLocator } from "../useGoogleMapsStoreLocator";
import { useNaverMapsStoreLocator } from "../useNaverMapsStoreLocator/useNaverMapsStoreLocator";
import { useStoreFilters } from "../useStoreFilters";
import { useTimedToggle } from "../useTimedToggle";
import { useYandexMapsStoreLocator } from "../useYandexMapsStoreLocator/useYandexMapsStoreLocator";

import type { UseStoreLocator } from "./useStoreLocator.types";

const SECOND = 1000;
const DEFAULT_MAP_INACTIVITY_TIMEOUT = 3 * SECOND;

export const useStoreLocator: UseStoreLocator = ({
  defaultLat,
  defaultLng,
  storesLimit,
  defaultZoom,
  activeStore,
  cartItems,
  filterByCartAvailabilityEnabled,
  categoryAttributeFilter,
  filtersTranslations,
  filtersEnabled,
  withinCategoryFilterOperator,
  isFiltersSharingEnabled = false,
  isMapPositionFromUrlEnabled = false,
  storeDetailsPage,
  storeDetailsPageTarget = "_blank",
  onChooseStore,
  activeStoreId,
  flotiqApiKey,
  flotiqContentType,
  defaultStoreIcon,
  storeIcons,
  isSearchVisible = true,
  isStoreCardAddressEnabled = false,
  isOpeningHoursEnabled = false,
  isOpeningHoursBreaksEnabled = false,
  isAvailableSticksEnabled = false,
  isMultiBrandSticksEnabled = false,
  isAvailableDevicesEnabled = false,
  isMultipleFiltersSectionOpenEnabled = false,
  isGeneralAnnouncementEnabled = false,
  isAvailableServicesEnabled = false,
  isStoreTypeEnabled = false,
  isVendingMachineEnabled = false,
  isVendingMachineTipEnabled = false,
  defaultOpenedFilterCategory,
  enabledFiltersCategories = {
    isOpenNowFilterEnabled: false,
    isProductColorsFilterEnabled: false,
    isProductFlavoursFilterEnabled: false,
    isProductFlavoursLYOFilterEnabled: false,
    isProductTypesFilterEnabled: true,
    isStoreServicesFilterEnabled: true,
    isStoreTypeFilterEnabled: false,
  },
  searchAdditionalTitle,
  isInputExtraTextVisible = false,
  isMapInteractionEnabled = false,
  isNearbyShopsEnabled = false,
  mapInactivityTimeout = DEFAULT_MAP_INACTIVITY_TIMEOUT,
  isAsianAddressFormatEnabled = false,
}) => {
  const { active: isMapActive, toggle: toggleIsMapActive, off: setMapInactive } = useTimedToggle(mapInactivityTimeout);
  const { setFlotiqConfig } = useFlotiqStorageConfig();

  const { locationServiceMapProvider } = useConfig();
  const mapStoreLocator = useMemo(() => {
    switch (locationServiceMapProvider) {
      case "google":
        return useGoogleMapsStoreLocator;
      case "naver":
        return useNaverMapsStoreLocator;
      case "yandex":
        return useYandexMapsStoreLocator;
      default:
        return useGoogleMapsStoreLocator;
    }
  }, [locationServiceMapProvider]);

  const flotiqStoresData = useFlotiqStores({
    cartItems,
    filterByCartAvailabilityEnabled,
    filtersTranslations,
    storesLimit: storesLimit,
    activeStore,
    activeStoreId,
    flotiqApiKey,
    flotiqContentType,
    enabledFiltersCategories,
    withinCategoryFilterOperator,
    isFiltersSharingEnabled,
    categoryAttributeFilter,
    isAsianAddressFormatEnabled,
  });

  const initialMapPosition = useMemo(() => {
    const initialData = getInitialDataFromURL(isFiltersSharingEnabled, isMapPositionFromUrlEnabled);
    const mapPosition = {
      defaultLat,
      defaultLng,
      defaultZoom,
    };

    if (initialData?.mapPosition) {
      const { lat, lng, zoom } = initialData.mapPosition;

      if (typeof lat === "number" && typeof lng === "number" && typeof zoom === "number") {
        mapPosition.defaultLat = lat.toString();
        mapPosition.defaultLng = lng.toString();
        mapPosition.defaultZoom = zoom;
      }
    }

    return mapPosition;
  }, [defaultLat, defaultLng, defaultZoom, isFiltersSharingEnabled, isMapPositionFromUrlEnabled]);

  const storeLocatorData = mapStoreLocator({
    ...initialMapPosition,
    activeStoreId: flotiqStoresData.activeStoreId,
    storeDetailsPage,
    handleSelectStore: flotiqStoresData.handleSelectStore,
    handleLoadFlotiqStores: flotiqStoresData.getFlotiqStores,
    isStoreDetails: !!activeStore,
    defaultStoreIcon,
    storeIcons,
    isSearchVisible,
    onActivity: toggleIsMapActive,
    onTap: setMapInactive,
    isNoStoresFilters: flotiqStoresData.isNoStoresFilters,
  });

  useEffect(() => {
    if (flotiqApiKey && flotiqContentType) {
      setFlotiqConfig({ flotiqApiKey, contentType: flotiqContentType });
    }
  }, [flotiqApiKey, flotiqContentType, setFlotiqConfig]);

  useEffect(() => {
    storeLocatorData.handleLoadMarkers(flotiqStoresData.flotiqStores || []);
  }, [flotiqStoresData.flotiqStores]);

  useEffect(() => {
    if (flotiqStoresData.userMarkerPosition) {
      storeLocatorData.handleLoadUserMarker(
        flotiqStoresData.userMarkerPosition.lat,
        flotiqStoresData.userMarkerPosition.lng
      );
    }
  }, [flotiqStoresData.userMarkerPosition]);

  const activeFiltersNumber = useMemo(() => {
    const { selectedFilters } = flotiqStoresData;
    return selectedFilters ? Object.values(selectedFilters)?.filter(({ selected }) => selected).length : 0;
  }, [flotiqStoresData]);

  const storeFilters = useStoreFilters({
    handleChangeFilters: flotiqStoresData.handleChangeFilters,
    isOpenNowFilterEnabled: enabledFiltersCategories.isOpenNowFilterEnabled ?? false,
    selectedFilters: flotiqStoresData.selectedFilters,
    allFilters: flotiqStoresData.newFilters,
  });

  return {
    ...flotiqStoresData,
    ...storeLocatorData,
    ...storeFilters,
    ...enabledFiltersCategories,
    handleSelectStore: storeLocatorData?.handleSelectStoreFromList ?? flotiqStoresData.handleSelectStore,
    storeDetailsPage,
    storeDetailsPageTarget,
    filtersEnabled,
    selectedFiltersNumber: activeFiltersNumber,
    searchAdditionalTitle,
    onChooseStore,
    isMultipleFiltersSectionOpenEnabled,
    defaultOpenedFilterCategory,
    isOpeningHoursEnabled,
    isOpeningHoursBreaksEnabled,
    isAvailableSticksEnabled,
    isMultiBrandSticksEnabled,
    isAvailableDevicesEnabled,
    isMapInteractionEnabled,
    isInputExtraTextVisible,
    isGeneralAnnouncementEnabled,
    isAvailableServicesEnabled,
    isStoreTypeEnabled,
    isVendingMachineEnabled,
    isVendingMachineTipEnabled,
    isStoreCardAddressEnabled,
    isNearbyShopsEnabled,
    isAsianAddressFormatEnabled,
    isMapActive: isMapActive && isMapInteractionEnabled,
    defaultZoom,
  };
};
